import { useState, useEffect, useRef, useMemo } from 'react';
import './device-io-settings.styles.scss';

import { connect } from 'react-redux';
import useSocket from '@lifepowr/components/src/components/socket/useSocket';

import { nanoid } from 'nanoid';
import { cloneDeep, merge } from 'lodash';

import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import CircularProgressLoader from '@lifepowr/components/src/components-shared/circular-progress-loader-component/circular-progress-loader.component';
import DeviceIOSettingsForm from './device-io-settings-form-component/device-io-settings-form.component';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';

import { nestedOmit } from '@lifepowr/components/src/utils';
import { emsModes, EmsMode, UNINITIALIZED_MODE_ID } from '@lifepowr/components/src/utils/emsModes';
import { useDeviceSchemaApiHook } from '../shared/device-data-api.hook';
import { ApiClientStatusEnum } from '@lifepowr/components/src/utils/api-client';
import { generateBatteryDefinitionSchemaMap } from '../shared/device-details-utils';
import AlertTitle from '@mui/material/AlertTitle';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import { getFlutterWebHostPath } from '../shared/flutter-integration';
import getClientInfo from '@lifepowr/components/src/clients';
import { SchemaLoadingFailureAlertBox } from '../shared/schema-failure-alert.component';
import { API } from 'Amplify';

function postProcessSchema(schema) {
  // Legacy schema had the "tariffStructure" at the root level
  if (schema.properties.tariffStructure) {
    schema.properties.tariffStructure.oneOf = schema.properties.tariffStructure.anyOf;
    delete schema.properties.tariffStructure.anyOf;
  }

  return schema;
}

async function setCfEmsMode(emsMode, thingName) {
  try {
    await API.put('IO_API', `/devices/${thingName}`, {
      body: {
        batControlMode: {
          mode: emsMode,
        },
      },
    });
  } catch (error) {
    throw new Error('Failed to set CloudFlex device EMS mode', { cause: error });
  }
}

/**
 * Settings list of the device.
 */
const DeviceIOSettings = (props) => {
  const { shadow, deviceId, connected, adminStore, isActivated } = props;

  const { publish = () => undefined } = useSocket();

  const { reported: { batControlMode, settings, apMode, identify } = {} } = shadow || {};

  const [schema, setSchema] = useState({});
  const [userPartnerCode, setUserPartnerCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorFetchingSchemas, setErrorFetchingSchemas] = useState(false);
  const batteryTitleToReferenceMap = useRef({});

  const thingName = deviceId;
  const { admin: isUserAnAdmin } = adminStore;

  // Due to PROD-828, we should allow non-WL partners to change EMS mode from Uninitialized to Normal only if the device is already activated
  const allowReplacedDevicesToRestoreEmsMode = isActivated && batControlMode?.mode === UNINITIALIZED_MODE_ID;

  const shouldAllowEmsModeChange = isUserAnAdmin || batControlMode?.mode !== UNINITIALIZED_MODE_ID || allowReplacedDevicesToRestoreEmsMode;

  const onEmsModeChange = async (e) => {
    const r = window.confirm('Do you really want to change Control Mode?');
    if (!r) {
      return 0;
    }

    // If the device has the cfu shadow, it's a cloudflex user device, so we need to send the batCtrMode via an API call
    if (shadow.desired.cfu) {
      try {
        await setCfEmsMode(e.target.value, thingName);
      } catch (error) {
        console.error(error);
        alert(error.message);
      }
    } else {
      const topic = `fleet/devices/${thingName}/state/set`;
      const pubObj = { batControlMode: { mode: Number(e.target.value) } };
      publish(topic, pubObj, true);
    }
  };

  const onApModeChange = async (_ev) => {
    const topic = `fleet/devices/${thingName}/state/set`;
    const pubObj = { '': { apMode: !apMode } };
    const result = await publish(topic, pubObj, true);

    console.log('DONE', result);
  };

  const onIdentifyModeChange = async (_ev) => {
    const topic = `fleet/devices/${thingName}/state/set`;
    const pubObj = { '': { identify: !identify } };
    const result = await publish(topic, pubObj, true);

    console.log('DONE', result);
  };

  const { deviceSchemaApiClientStatus, setThingName, triggerDeviceSchema } = useDeviceSchemaApiHook();

  useEffect(() => {
    const clientInfo = getClientInfo();
    if (clientInfo?.partnerCode) {
      setUserPartnerCode(clientInfo.partnerCode);
    }

    setThingName(thingName);
  }, [thingName]);

  useEffect(() => {
    setLoading(deviceSchemaApiClientStatus.clientStatus === ApiClientStatusEnum.Busy);
    setErrorFetchingSchemas(deviceSchemaApiClientStatus.clientStatus === ApiClientStatusEnum.Error);

    if (deviceSchemaApiClientStatus.clientStatus === ApiClientStatusEnum.Done) {
      const result = postProcessSchema(deviceSchemaApiClientStatus.data);
      batteryTitleToReferenceMap.current = generateBatteryDefinitionSchemaMap(deviceSchemaApiClientStatus.data);

      setSchema(result);
    }
  }, [deviceSchemaApiClientStatus.clientStatus]);

  const deviceInfoPath = useMemo(() => {
    const isWL = !!userPartnerCode;

    return `${getFlutterWebHostPath(isWL)}/?tn=${thingName}`;
  }, [thingName, userPartnerCode]);

  //Function that sends a request state change when a new settings object is required
  const onSubmit = (data) => {
    const mergedSettings = merge(cloneDeep(settings), data);
    const topic = `fleet/devices/${thingName}/state/set`;
    const clientToken = nanoid();
    const pubObj = { settings: mergedSettings, clientToken };

    publish(topic, pubObj, true);
  };

  const formProps = {
    schema,
    formData: nestedOmit(settings || {}),
    onSubmit,
    batteryTitleToReferenceMap: batteryTitleToReferenceMap.current,
  };

  const modes = Object.entries(emsModes)
    .map(([emsOptionModeId, emsMode]) => {
      const { isModeSettableByAdmins, isModeSettableByPartners } = emsMode;

      // biome-ignore lint/suspicious/noGlobalIsNan: isNaN also converts to number, this is needed in this case
      const emsCurrentModeId = isNaN(batControlMode?.mode) ? '' : batControlMode.mode;
      const shouldRenderThisEmsMode =
        (Boolean(isUserAnAdmin) && isModeSettableByAdmins) || (!isUserAnAdmin && isModeSettableByPartners) || Number(emsOptionModeId) === emsCurrentModeId;

      if (!shouldRenderThisEmsMode) {
        return null;
      }

      return (
        <MenuItem
          value={Number(emsOptionModeId)}
          style={{ pointerEvents: 'auto' }}
          key={emsOptionModeId}
        >
          <EmsMode mode={Number(emsOptionModeId)} />
        </MenuItem>
      );
    })
    .filter((m) => m);

  const apControl = (
    <Switch
      checked={apMode}
      onChange={onApModeChange}
    />
  );
  const identifyControl = (
    <Switch
      checked={identify}
      onChange={onIdentifyModeChange}
    />
  );
  const sliders = connected ? (
    <>
      <FormControlLabel
        control={apControl}
        label="AP Mode"
        className="device-io-settings__ap-mode"
      />
      <FormControlLabel
        control={identifyControl}
        label="Identify"
        className="device-io-settings__ap-mode"
      />
    </>
  ) : null;

  return (
    <div
      className="device-io-settings"
      style={{ height: '100%' }}
    >
      {isUserAnAdmin && (
        <Alert
          severity="warning"
          sx={{ my: 2, mx: 0.5, boxShadow: 2 }}
        >
          <AlertTitle>Please use the new Admin Dashboard</AlertTitle>
          <Typography
            paragraph
            sx={{ mb: 0 }}
          >
            Please make IO settings changes via&nbsp;
            <Link
              target="_blank"
              rel="noopener noreferrer"
              href="https://admin.lifepowr.eu"
              sx={{ color: 'black', fontWeight: 'bold', ':hover': { color: 'black' } }}
            >
              admin.lifepowr.eu
            </Link>
            . After making changes, make sure to click <strong>Sync to device</strong>.
          </Typography>
        </Alert>
      )}

      {loading ? (
        <div className="device-io-settings__form-loader">
          <CircularProgressLoader />
        </div>
      ) : (
        <div className="device-io-settings__form-wrapper">
          {!shouldAllowEmsModeChange && (
            <Alert
              severity="info"
              sx={{ my: 2, mx: 0.5, boxShadow: 2 }}
            >
              <AlertTitle>Deprecation Notice</AlertTitle>
              <Typography paragraph>
                You can no longer activate the EMS Steering (by changing the EMS Mode from Uninitialized to Normal) via this page. After the sign off action
                (via the&nbsp;
                <Link
                  target="_blank"
                  href={deviceInfoPath}
                  sx={{ color: 'black', fontWeight: 'bold', ':hover': { color: 'black' } }}
                >
                  <strong>Device Info Page</strong>
                </Link>
                ) the EMS mode will automatically be set to Mode Normal when User registration {userPartnerCode ? '(EAN) ' : ''}is completed.
                <br />
                {!userPartnerCode ? (
                  <Link
                    target="_blank"
                    href="https://docs.lifepowr.io/installer/v1/device-activation"
                    sx={{ color: 'black', fontWeight: 'bold', ':hover': { color: 'black' } }}
                  >
                    Link to documentation
                  </Link>
                ) : null}
              </Typography>
            </Alert>
          )}

          <FormControl
            className="device-io-settings__ems-wrapper"
            variant="outlined"
          >
            <InputLabel id="battery-mode-label">Battery EMS Mode</InputLabel>

            <Select
              labelId="battery-mode-label"
              label="Battery EMS Mode"
              onChange={onEmsModeChange}
              // biome-ignore lint/suspicious/noGlobalIsNan: isNaN also converts to number, this is needed in this case
              value={isNaN(batControlMode.mode) ? '' : batControlMode.mode}
              className="device-io-settings__mode-select"
              disabled={!shouldAllowEmsModeChange}
              sx={{
                backgroundColor: 'white',
              }}
            >
              {modes}
            </Select>

            {sliders}
          </FormControl>

          {errorFetchingSchemas ? (
            <SchemaLoadingFailureAlertBox onRefresh={triggerDeviceSchema} />
          ) : (
            <>
              <Divider className="device-io-settings__ems-divider" />

              {isUserAnAdmin && <DeviceIOSettingsForm {...formProps} />}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const map = (state) => {
  const adminStore = state?.admin;

  const { connected, shadow } = state.system || {};

  return {
    shadow,
    connected,
    adminStore,
    isActivated: !!state.system?.attributes?.activatedOn,
  };
};

export default connect(map, null, null)(DeviceIOSettings);
